import EventEmitter from "eventemitter3";
import RowList from "../grid/row-model";
import { Helper } from "../../helper";
import EpgChannel from "./epg-channel";
import EPGUtility from "./epg-utility";
import { EPG_STATUS } from "./epg-enum";
import { REQUEST_STATUS } from "../../enum";
import { fetchMultiRequest } from "../../models/data-service";
import EpgProgram from "./epg-program";

export default class EPGManager extends EventEmitter {

    url = ''
    channelRowList!: RowList
    rowLists: RowList[] = []
    currIndex = 0
    visibleItems = 3
    bufferItems = 2
    EPGChannelList: EpgChannel[] = []
    timerange
    status: EPG_STATUS = EPG_STATUS.NONE
    currTimeId:any;
    currDate:Date = new Date();
    channelInfo: {
        prev:EpgProgram|null,
        next:EpgProgram|null
    } = {prev:null, next:null}

    currentPlayingItem = {
        channelIndex: 0
    }
    loaded: boolean = false

    private _programCache: Map<string, EpgProgram[]> = new Map()
    private readonly MAX_CACHE_SIZE = 200
    private _cacheTimeKey: number = 0
    private _allVideosCache: any[] | null = null
    private _prefetchTimer: any = null
    private _generation = 0

    constructor(url = '', channelRowList: RowList | RowList[] | null = null) {
        super();
        this.url = url
        if (Array.isArray(channelRowList)) {
            this.rowLists = channelRowList
            this.channelRowList = channelRowList[0]
        } else if (channelRowList) {
            this.rowLists = [channelRowList]
            this.channelRowList = channelRowList
        }
        this.timerange = this.getTimeRange()
    }


    /**
     * Initialize function will be call for the first time when the
     * user selects any live item from grid. and will pass the current index of the item
     * This function will create the filtered time range for epg filtering and creation.
     * This functino will fetch the epg based on current index and buffer rows. for faster display.
     *
     * @param {number} [initialIndex=0]
     */
    async init(initialIndex = 0) {
        const isValidUrl = !!this.url
        const isValidRowList = (this.rowLists?.length ?? 0) > 0
        this.currIndex = initialIndex
        this.timerange = this.getTimeRange()
        if (isValidRowList) {
            this.getEpgList()
        } else if (isValidUrl) {
            this.fetchInitialUrl()
        }
    }

    /**
     * Schedules the next current-time tick at the start of the next minute boundary.
     * Replaces setInterval so the callback fires exactly once per minute instead of
     * checking 60 times per minute whether a minute has elapsed.
     */
    startCurrentTimer() {
        const msToNextMinute = 60_000 - (Date.now() % 60_000)
        this.currTimeId = setTimeout(() => {
            this.updateCurrentTime()
            this.startCurrentTimer()
        }, msToNextMinute)
    }

    /**
     * Stops the current-time timer.
     */
    stopCurrentTimer() {
        clearTimeout(this.currTimeId)
    }

    /**
     * function will check for any change in program from epg or if user changes the channel.
     * which updates the program of current playing and the next changed program     *
     *
     * @param {boolean} [force=false]
     */
    updateCurrentTime(force=false) {
        const newDate = EPGUtility.getDeviceCurrentDateTime()
        newDate.setSeconds(0,0)
        if(force) {
            this.currDate = newDate
            this.dispatchEvent('onCurrentTime',{currDate: this.currDate})
            this.updateProgramInfo()
        } else {
            if((newDate-this.currDate)/1000/60 >= 1) {
                this.currDate = newDate
                this.dispatchEvent('onCurrentTime', {currDate: this.currDate})
                this.updateProgramInfo()
            }
        }

    }

    /**
     * updates the current program when change is happend in current channel or user selects different channel
     */
    updateProgramInfo(forceDispatchInitialProgram?:boolean) {
        const currPlayingRow = this.EPGChannelList[this.currentPlayingItem.channelIndex]
        const tprogram:EpgProgram = currPlayingRow?.getNowPlayingProgram(this.currDate)
        let program = new EpgProgram()
        if (!this._allVideosCache) {
            this._allVideosCache = this.rowLists.flatMap(r => r.videos)
        }
        const rowItem = this._allVideosCache[this.currentPlayingItem.channelIndex]
        if(!rowItem) return
        if(!tprogram || tprogram.title == 'No Data') {
            program.title = rowItem.title
            program.type = 'post'
            program.actiontype = 'live'
            program.node_id = rowItem?.node_id
            program.uid = rowItem?.uid
            program.runtime = rowItem?.runtime
            program.actionkey = rowItem?.actionkey
        } else {
            program = {...tprogram}
            program.type = 'post'
            program.actiontype = 'live'
            program.node_id = program.id
            program.uid = (program.uid || 0)
            program.runtime = program.duration
            program.actionkey = program.actionkey || (rowItem?.actionkey)
        }
        const hasChanged = !this.channelInfo.next || this.channelInfo.next.id !== program.id
        if (hasChanged) {
            if (this.channelInfo.next) {
                this.channelInfo.prev = this.channelInfo.next
            }
            this.channelInfo.next = program
        }
        if (hasChanged || forceDispatchInitialProgram) {
            this.dispatchEvent('onProgramChanged', {channelInfo: this.channelInfo})
        }
        this.dispatchEvent('onTimeupdate', null)
    }

    /**
     * call when the user changes channels
     */
    changeChannel(index:number, forceDispatchInitialProgram?:boolean) {
        this.currIndex = index
        this.currentPlayingItem.channelIndex = index
        if(this.loaded) {
            this.updateProgramInfo(forceDispatchInitialProgram)
        }
    }

    /**
     * Switch to a new row. Replaces the active row list, resets channel data
     * and re-fetches EPG programs for the new row.
     *
     * @param {RowList} newRowList - The RowList of the new row to display
     * @param {number} [initialIndex=0] - The local channel index to select after loading
     */
    async switchRow(newRowList: RowList, initialIndex = 0) {
        // Cancel any pending prefetch from the old row.
        clearTimeout(this._prefetchTimer)
        this._prefetchTimer = null

        this.loaded = false
        this.stopCurrentTimer()
        this.rowLists = [newRowList]
        this.channelRowList = newRowList
        this.currIndex = initialIndex
        this.EPGChannelList = []
        this.channelInfo = { prev: null, next: null }
        this._allVideosCache = null
        await this.getEpgList()
    }

    /**
     * fetch the row channels and fetch the epg url for the current thumbnail is selected from grid.
     */
    async fetchInitialUrl() {
        const rowStyle: { row_height: number, row_item_height: number, row_item_width: number, rowstyle: string } = Helper.getRowStyle()
        const rowData = {
            description: 'live',
            dynamic_deeplink: '',
            feed_type: 'show',
            name: 'LIVE TV',
            row_height: rowStyle.row_height,
            row_item_height: rowStyle.row_item_height,
            row_item_width: rowStyle.row_item_width,
            rowstyle: rowStyle.rowstyle,
            row_sub_type: '',
            row_type: 'live',
            url: this.url
        }
        this.channelRowList = new RowList(rowData)
        this.rowLists = [this.channelRowList]
        this._allVideosCache = null
        await this.channelRowList.fetchMoreVideos(0)
        await this.getEpgList()
    }

    private _validateCache() {
        const key = this.timerange.startDate.getTime()
        if (this._cacheTimeKey !== key) {
            this._programCache.clear()
            this._cacheTimeKey = key
        }
    }

    async getEpgList() {
        const myGeneration = ++this._generation
        this._validateCache()
        const channelList = this.loaded ? this.EPGChannelList : await this.fetchEpgList()
        await this.fetchProgramDetails(channelList, this.currIndex)
        if (myGeneration !== this._generation) return // superseded by a newer getEpgList() call
        this.EPGChannelList = channelList
        this.currentPlayingItem.channelIndex = this.currIndex
        this.dispatchEvent('onEpgLoaded', null)
        this.loaded = true
        this.changeChannel(this.currIndex, true)
        this.updateCurrentTime(true)
        this.stopCurrentTimer()
        this.startCurrentTimer()
    }


    /**
     * function to dispatch a custom event. to the listener here it is the video-screen-live class.
     *
     * @param {string} eventType
     * @param {*} data
     */
    dispatchEvent(eventType:string, data:any) {
        const event = new CustomEvent(eventType,{
            detail: data
        })
        this.emit(eventType, event)
    }


    /**
     * Fetches all channel pages from every rowList in parallel before EPGChannelList
     * is built. All pages are awaited via Promise.all so no channels are missed.
     */
    async fetchAllChannels() {
        const fetchListPromise: Promise<RowList>[] = []
        for (const rowList of this.rowLists) {
            let index = 0
            while (index <= rowList.totalPages - 1) {
                if (!rowList.fetchedPageList[index]) {
                    fetchListPromise.push(rowList.fetchMoreVideos(index))
                }
                index++
            }
        }
        await Promise.all(fetchListPromise)
        return true
    }

    /**
     * fetch the channel nodes and then creates the epg lists and returns
     *
     * @async
     * @returns {unknown}
     */
    async fetchEpgList() {
        await this.fetchAllChannels()
        let globalIndex = 0
        const epgList: EpgChannel[] = []
        for (const rowList of this.rowLists) {
            rowList.videos.forEach(item => {
                const epgChannel: EpgChannel = new EpgChannel()
                epgChannel.index = globalIndex++
                epgChannel.epgType = item?.epg_type
                epgChannel.name = item?.title
                epgChannel.rowName = rowList.name
                epgChannel.epgUrl = item?.epg_url
                epgChannel.squareIcon = item?.squareIcon
                epgChannel.main_picture = item?.main_picture
                const cachedPrograms = epgChannel.epgUrl ? this._programCache.get(epgChannel.epgUrl) : undefined
                if (cachedPrograms) {
                    epgChannel.programList = cachedPrograms
                    epgChannel.fetchStatus = REQUEST_STATUS.OK
                    epgChannel.fetchPromise.resolve(epgChannel)
                }
                epgList.push(epgChannel)
            })
        }
        return epgList
    }

    /**
     * finds the index of channel where epg is not yet requested
     * based on current selected.
     * if the current selected channel is not yet fetched it will return the current index.
     * if the current selected is already fetch (totally 6 epg will fetched as batch.)
     * then the next will be the 7 hence it will return 7.
     *
     * @param {*} currIndex
     * @param {*} prevIndex
     */
    getNextPrefetchingIndex(currIndex, prevIndex) {
        if (currIndex == prevIndex) return

        let nextFetchingIndex = currIndex
        let exit = false
        const incr = currIndex > prevIndex ? 1 : -1
        let shouldFetch = false
        while (!exit) {
            nextFetchingIndex += incr
            if (this.EPGChannelList[nextFetchingIndex]?.fetchStatus == REQUEST_STATUS.NONE) {
                shouldFetch = true
                exit = true
            } else if (
                nextFetchingIndex >= this.EPGChannelList.length ||
                nextFetchingIndex < 0
            ) {
                exit = true
            }
        }
        if (!shouldFetch) return
        clearTimeout(this._prefetchTimer)
        this._prefetchTimer = setTimeout(() => this.fetchProgramDetails(this.EPGChannelList, nextFetchingIndex), 150)
    }

    /**
     * Fetches the epg program details based on getNextPrefetchIndex.
     *
     * @async
     * @param {EpgChannel[]} channelList - list to fetch against (may not be committed to
     *   this.EPGChannelList yet — see getEpgList()'s atomic commit)
     * @param {number} [index=0]
     * @returns {*}
     */
    async fetchProgramDetails(channelList: EpgChannel[], index = 0) {
        const fetchIndexList = this.getPrefetchIndexList(channelList, index, this.visibleItems, this.bufferItems)
        const fetchData: Promise<EpgChannel>[] = []
        const fetchQueue: { channel: EpgChannel, url: string }[] = []
        const seenUrls = new Set<string>()
        const dedupedChannels: EpgChannel[] = []

        fetchIndexList.forEach((value: number) => {
            const channel = channelList[value]
            if (channel?.fetchStatus == REQUEST_STATUS.NONE) {
                const cached = channel.epgUrl ? this._programCache.get(channel.epgUrl) : undefined
                if (cached) {
                    channel.programList = cached
                    channel.fetchStatus = REQUEST_STATUS.OK
                    channel.fetchPromise.resolve(channel)
                    return
                }
                if (channel.epgUrl && seenUrls.has(channel.epgUrl)) {
                    channel.fetchStatus = REQUEST_STATUS.FETCHING
                    dedupedChannels.push(channel)
                    return
                }
                if (channel.epgUrl) seenUrls.add(channel.epgUrl)
                fetchQueue.push({ channel, url: channel.epgUrl })
                channel.fetchStatus = REQUEST_STATUS.FETCHING
            }
        })

        if (fetchQueue.length > 0) {
            const results = await fetchMultiRequest(fetchQueue.map(item => item.url))
            results.data.forEach((data: EpgProgram[], i: number) => {
                const { channel } = fetchQueue[i] ?? {}
                if (channel) {
                    fetchData.push(channel.fetchEpgPrograms(this.timerange, data))
                }
            })
        }

        if (fetchData.length > 0) {
            await Promise.all(fetchData)
            fetchQueue.forEach(({ channel: ch }) => {
                if (ch.fetchStatus === REQUEST_STATUS.OK && ch.epgUrl) {
                    if (this._programCache.size >= this.MAX_CACHE_SIZE) {
                        this._programCache.delete(this._programCache.keys().next().value)
                    }
                    this._programCache.set(ch.epgUrl, ch.programList)
                }
            })
        } else if (fetchQueue.length > 0) {
            fetchQueue.forEach(({ channel: ch }) => {
                if (ch.fetchStatus === REQUEST_STATUS.FETCHING) {
                    ch.fetchStatus = REQUEST_STATUS.FAIL
                    ch.fetchPromise.resolve(ch)
                }
            })
        }

        dedupedChannels.forEach(ch => {
            const cached = ch.epgUrl ? this._programCache.get(ch.epgUrl) : undefined
            if (cached) {
                ch.programList = cached
                ch.fetchStatus = REQUEST_STATUS.OK
                ch.fetchPromise.resolve(ch)
            } else {
                ch.fetchStatus = REQUEST_STATUS.FAIL
                ch.fetchPromise.resolve(ch)
            }
        })
    }

    /**
     * This function will return a array of index to be fetched as batch.
     *
     * @param {any[]} orginalArray
     * @param {number} focusIndex
     * @param {number} visibleCount
     * @param {number} bufferCount
     * @returns {number[]}
     */
    getPrefetchIndexList(orginalArray: any[], focusIndex: number, visibleCount: number, bufferCount: number): number[] {
        const st = focusIndex - bufferCount
        const ed = focusIndex + visibleCount + bufferCount
        const arr: Set<number> = new Set()
        const len = orginalArray.length
        for (let i = st; i < ed; i++) {
            arr.add(((i % len) + len) % len)
        }
        return Array.from(arr)
    }

    /**
     * return the time range (1 day +/- 3hrs ) for epg time range display
     * and filter the programs to be displayed in epg.
     *
     * @returns {{ startDate: Date, endDate: Date }}
     */
    getTimeRange(): { startDate: Date, endDate: Date } {
        const startTime: Date = EPGUtility.getDeviceCurrentDateTime()
        startTime.setMinutes(0, 0, 0)
        startTime.setHours(startTime.getHours())
        const endTime = new Date(startTime)
        endTime.setHours(startTime.getHours() + 24)
        return {
            startDate: startTime,
            endDate: endTime
        }
    }

    destroy() {
        clearTimeout(this._prefetchTimer)
        this.stopCurrentTimer()
        this.removeAllListeners()
    }
}
